import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Capacitor } from '@capacitor/core'
import './index.css'
import App from './App'
import { AuthProvider } from './lib/auth'
import { I18nProvider } from './i18n'
import { TVApp } from './tv/TVApp'
import { ColorfulTvApp } from './tv/ColorfulTvApp'
import { WordTvApp } from './tv/WordTvApp'
import { ThreeStepTvApp } from './tv/ThreeStepTvApp'
import { JoinApp } from './join/JoinApp'
import { AdminUsage } from './components/admin/AdminUsage'
import { AdminLive } from './components/admin/AdminLive'
import { PrivacyPolicy } from './components/PrivacyPolicy'

/**
 * Snapshot env(safe-area-inset-*) into CSS custom properties, with a
 * device floor so the layout can NEVER fully collapse.
 *
 * Why a floor: on iOS we set `contentInset: 'never'` + StatusBar
 * `overlaysWebView`, so CSS env(safe-area-inset-*) is the ONLY source of
 * inset — there's no native cushion. In a wedged / slow-start state
 * (seen with flaky Wi-Fi) WKWebView reports env() = 0 for top AND bottom
 * for the whole session, so the header slides under the status bar and
 * the tab bar sits on the screen edge until a force-quit. Falling back to
 * live env() doesn't help when env() itself is the thing reporting 0.
 *
 * The fix: a notched iPhone ALWAYS has ≥44pt top and 34pt bottom insets.
 * We take max(measured, floor). In normal operation env() reports the
 * exact value (≥ floor), so max() returns the real number — no
 * over-padding. When env() wrongly reads 0, max() returns the floor and
 * the layout stays intact. Portrait-locked on iPhone, so top/bottom never
 * legitimately hit 0. SE (no notch) and iPad get no floor and behave as
 * before via env().
 */
function safeAreaFloors(): { top: number; bottom: number } {
  if (Capacitor.getPlatform() !== 'ios') return { top: 0, bottom: 0 }
  const w = window.screen.width, h = window.screen.height
  // ~19.5:9 Face-ID iPhones sit at ~2.16; SE is 1.78, iPad ~1.33. A ratio
  // over 2 uniquely identifies a notch/Dynamic-Island iPhone.
  const ratio = Math.max(w, h) / Math.min(w, h)
  return ratio > 2 ? { top: 44, bottom: 34 } : { top: 0, bottom: 0 }
}

// Set the floor synchronously at module load so the very first paint is
// already inset — no collapsed-layout flash before the snapshot runs.
const FLOORS = safeAreaFloors()
if (typeof document !== 'undefined' && (FLOORS.top || FLOORS.bottom)) {
  document.documentElement.style.setProperty('--sat-top', `${FLOORS.top}px`)
  document.documentElement.style.setProperty('--sat-bottom', `${FLOORS.bottom}px`)
}

function snapshotSafeAreaInsets() {
  const sides = ['top', 'right', 'bottom', 'left'] as const
  for (const side of sides) {
    const probe = document.createElement('div')
    probe.style.cssText =
      `position:fixed;${side}:0;height:env(safe-area-inset-${side});width:env(safe-area-inset-${side});visibility:hidden;pointer-events:none;`
    document.body.appendChild(probe)
    const dim = side === 'top' || side === 'bottom'
      ? probe.getBoundingClientRect().height
      : probe.getBoundingClientRect().width
    document.body.removeChild(probe)
    const floor = side === 'top' ? FLOORS.top : side === 'bottom' ? FLOORS.bottom : 0
    const val = Math.max(dim, floor)
    // With a floor, val is always ≥ floor (>0) on notched iPhones, so the
    // var is always set. For sides with no floor (left/right, or non-
    // notched top/bottom) a 0 leaves the var unset → CSS falls back to
    // live env(), which is correct on those devices.
    if (val > 0) {
      document.documentElement.style.setProperty(`--sat-${side}`, `${val}px`)
    }
  }
}
function attachInsetListeners() {
  // Boot-time snapshots — retry with backoff so a slow StatusBar-overlay
  // apply still gets picked up. The floor already holds the layout from
  // the first paint; these upgrade to the exact env() value once it's live.
  snapshotSafeAreaInsets()
  for (const delay of [250, 1000, 3000]) {
    setTimeout(snapshotSafeAreaInsets, delay)
  }
  // Catch every subsequent change (keyboard, dynamic island, iPad rotate).
  window.addEventListener('resize', snapshotSafeAreaInsets)
  window.addEventListener('orientationchange', snapshotSafeAreaInsets)
}
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachInsetListeners, { once: true })
  } else {
    attachInsetListeners()
  }
}

/**
 * Global Enter-key behaviour for text inputs and textareas.
 *
 *   <textarea>            Enter inserts a newline by default. None of
 *                         this app's textareas want multi-line input
 *                         (every prompt is a short, single-thought
 *                         action / step / dish title). On mobile users
 *                         expect Enter to dismiss the keyboard, not
 *                         add an empty paragraph. We preventDefault
 *                         the newline and call .blur() — iOS dismisses
 *                         the soft keyboard whenever the focused
 *                         element loses focus.
 *
 *   <input type="text">   Enter natively submits a parent <form>.
 *                         We DON'T preventDefault — that breaks the
 *                         room-code join shortcut etc. We just call
 *                         .blur() too, so even if the input isn't in
 *                         a form, the keyboard still goes down.
 *
 * Attached at the document level (capture phase) so a single listener
 * covers every input across the app — the alternative is touching
 * ~25 input/textarea sites.
 */
if (typeof document !== 'undefined') {
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key !== 'Enter') return
    const t = e.target
    if (t instanceof HTMLTextAreaElement) {
      e.preventDefault()
      t.blur()
    } else if (t instanceof HTMLInputElement) {
      const dismissable = ['text','search','email','tel','url','number','password']
      if (dismissable.includes(t.type)) t.blur()
    }
  })
}

/**
 * Entry router. Surfaces live in one build, picked by pathname:
 *
 *   /                       → main phone/iOS app (requires sign-in)
 *   /tv, /tv/XXX            → I'd Eat That TV display (anonymous auth)
 *   /tv/colorful[/XXX]      → Colorful TV display (anonymous auth)
 *   /tv/three-step[/XXX]    → Three Step Solution TV display (anonymous auth)
 *   /join, /join/XXX        → phone guest join (anonymous auth)
 *   /privacy                → public legal page (no auth — Apple reviewer
 *                             must reach it from a cold load)
 *
 * Pathname match rather than a route tree — each surface is a different
 * shell with its own auth flow and visual density.
 */
function pickRoot(): React.JSX.Element {
  const path = window.location.pathname
  if (path === '/privacy')       return <PrivacyPolicy />
  if (path.startsWith('/tv/colorful')   || path === '/tv/colorful/')   return <ColorfulTvApp />
  if (path.startsWith('/tv/three-step') || path === '/tv/three-step/') return <ThreeStepTvApp />
  if (path.startsWith('/tv/fake-out') || path.startsWith('/tv/close-enough')) return <WordTvApp />
  if (path.startsWith('/tv'))    return <TVApp />
  if (path.startsWith('/join'))  return <JoinApp />
  if (path.startsWith('/admin/live'))  return <AdminLive />
  if (path.startsWith('/admin/usage')) return <AdminUsage />
  return <App />
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AuthProvider>
      <I18nProvider>
        {pickRoot()}
      </I18nProvider>
    </AuthProvider>
  </StrictMode>,
)
